
import { ExtractionRule } from '../types';
import { JSONPath } from 'jsonpath-plus';

const castValue = (value: any, type?: string) => {
    if (value === undefined || value === null) return null;
    if (!type || type === 'auto') return value;

    switch (type) {
        case 'string': return String(value);
        case 'number': return Number(value);
        case 'boolean': return Boolean(value);
        case 'object': return typeof value === 'object' ? value : { value };
        case 'array': return Array.isArray(value) ? value : [value];
        default: return value;
    }
};

/**
 * Heuristic to detect content type for UI display
 */
export const detectContentType = (content: string): 'JSON' | 'XML' | 'Text' => {
    if (!content || typeof content !== 'string') return 'Text';
    const trimmed = content.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) return 'JSON';
    if (trimmed.startsWith('<')) return 'XML';
    return 'Text';
};

/**
 * Evaluate a JSONPath expression against parsed JSON data.
 * Uses jsonpath-plus which supports the full JSONPath spec including
 * filters ($..book[?(@.price<10)]), recursive descent, array slicing, etc.
 */
const evaluateJsonPath = (json: any, path: string): any => {
    // Normalize: if the user typed a simple dot-path without $, prepend $.
    let normalizedPath = path.trim();
    if (!normalizedPath.startsWith('$')) {
        normalizedPath = '$.' + normalizedPath;
    }

    const results = JSONPath({ path: normalizedPath, json, wrap: true });
    if (!results || results.length === 0) return undefined;
    // Return single value unwrapped, array if multiple matches
    return results.length === 1 ? results[0] : results;
};

/**
 * Evaluate an XPath expression against an XML document.
 * Uses the browser-native document.evaluate (XPath 1.0).
 */
const evaluateXPath = (doc: Document, expression: string): any => {
    // Try real XPath first
    try {
        const result = doc.evaluate(expression, doc, null, XPathResult.ANY_TYPE, null);

        switch (result.resultType) {
            case XPathResult.NUMBER_TYPE:
                return result.numberValue;
            case XPathResult.STRING_TYPE:
                return result.stringValue;
            case XPathResult.BOOLEAN_TYPE:
                return result.booleanValue;
            default: {
                // Node iterator — collect text content
                const values: string[] = [];
                let node = result.iterateNext();
                while (node) {
                    values.push(node.textContent || '');
                    node = result.iterateNext();
                }
                if (values.length === 0) return undefined;
                return values.length === 1 ? values[0] : values;
            }
        }
    } catch {
        // XPath failed — fall back to querySelector for CSS-style selectors
        try {
            const node = doc.querySelector(expression);
            return node ? node.textContent : undefined;
        } catch {
            return undefined;
        }
    }
};

export const extractData = (
    content: string,
    rules: ExtractionRule[]
): Record<string, any> => {
    const results: Record<string, any> = {};

    if (!rules || rules.length === 0) return results;
    if (!content || typeof content !== 'string') return results;

    // Cache parsed versions to avoid re-parsing for every rule
    let cachedJson: any = null;
    let jsonAttempted = false;
    let cachedXml: Document | null = null;
    let xmlAttempted = false;

    const getJson = () => {
        if (jsonAttempted) return cachedJson;
        jsonAttempted = true;
        try {
            cachedJson = JSON.parse(content);
        } catch {
            cachedJson = null;
        }
        return cachedJson;
    };

    const getXml = () => {
        if (xmlAttempted) return cachedXml;
        xmlAttempted = true;
        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(content, "text/xml");
            // Check for parse errors
            if (doc.querySelector('parsererror')) {
                cachedXml = null;
            } else {
                cachedXml = doc;
            }
        } catch {
            cachedXml = null;
        }
        return cachedXml;
    };

    rules.forEach(rule => {
        try {
            let extractedValue: any = undefined;

            switch (rule.method) {
                case 'jsonpath': {
                    const json = getJson();
                    if (json) {
                        extractedValue = evaluateJsonPath(json, rule.source);
                    }
                    break;
                }

                case 'xpath': {
                    const xml = getXml();
                    if (xml) {
                        extractedValue = evaluateXPath(xml, rule.source);
                    }
                    break;
                }

                case 'regex':
                    try {
                        const regex = new RegExp(rule.source);
                        const match = content.match(regex);
                        if (match) {
                            extractedValue = match[1] !== undefined ? match[1] : match[0];
                        }
                    } catch {
                        console.warn(`Regex failed: ${rule.source}`);
                    }
                    break;

                default:
                    // Unknown extraction method — leave extractedValue undefined
                    // so the defaultValue/null fallthrough below handles the rule.
                    break;
            }

            if (extractedValue !== undefined && extractedValue !== null) {
                results[rule.targetVar] = castValue(extractedValue, rule.type);
            } else if (rule.defaultValue !== undefined && rule.defaultValue !== null && rule.defaultValue !== '') {
                results[rule.targetVar] = castValue(rule.defaultValue, rule.type);
            } else {
                results[rule.targetVar] = null;
            }

        } catch (error) {
            // Record the failure on the result set instead of silently
            // dropping it. Previously this only logged to console, which
            // made failed extractions indistinguishable from rules that
            // legitimately produced `null`.
            console.error(`Error applying rule ${rule.targetVar}`, error);
            results[rule.targetVar] = null;
        }
    });

    return results;
};
